import React from "react";

import { useLocalization } from "@fluent/react";
import { Alert, Button, Row, Spinner } from "@narmi/design_system";

import { useUploadSession } from "../uploadSession/useUploadSession";

import styles from "./UploadProgressSection.module.scss";

type UploadProgressSectionProps = {
  onOpenDialog: () => void;
};

const UploadProgressSection = ({
  onOpenDialog,
}: UploadProgressSectionProps) => {
  const { l10n } = useLocalization();
  const { counters, dismissed, dismissProgress } = useUploadSession();
  const { phase, total, resolved, pending, unmatched, stalled } = counters;
  // A receipt the poll gave up on needs a look just like a failed file does.
  const attention = counters.attention + stalled;

  if (phase === "idle") return null;

  if (phase === "in_progress") {
    return (
      <div
        className={`${styles.section} ${styles.panel}`}
        aria-live="polite"
        data-testid="receipts-upload-progress-in-progress"
      >
        <Row gapSize="s">
          <Row.Item shrink>
            <Spinner size={20} strokeWidth={2} color="var(--theme-primary)" />
          </Row.Item>
          <Row.Item>
            <div className={styles.content}>
              <span className="fontColor--primary fontSize--s">
                {l10n.getString("receipts-upload-progress-in-progress", {
                  resolved,
                  total,
                })}
              </span>
              <span className={styles.link}>
                <Button
                  kind="plain"
                  size="s"
                  label={l10n.getString("receipts-upload-progress-view")}
                  onClick={onOpenDialog}
                  testId="receipts-upload-progress-view"
                />
              </span>
            </div>
          </Row.Item>
        </Row>
      </div>
    );
  }

  if (dismissed) return null;

  if (attention > 0) {
    return (
      <div
        className={styles.section}
        data-testid="receipts-upload-progress-attention"
      >
        <Alert
          kind="warn"
          icon="alert-circle"
          paddingSize="s"
          isActive
          isDismissable
          onUserDismiss={dismissProgress}
        >
          <div className={styles.content}>
            <span className="fontSize--s">
              {l10n.getString("receipts-upload-progress-attention", {
                resolved,
                attention,
              })}
            </span>
            <span className={styles.link}>
              <Button
                kind="plain"
                size="s"
                label={l10n.getString("receipts-upload-progress-review")}
                onClick={onOpenDialog}
                testId="receipts-upload-progress-review"
              />
            </span>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className={styles.section}
      data-testid="receipts-upload-progress-success"
    >
      <Alert
        kind="success"
        icon="info"
        paddingSize="s"
        isActive
        isDismissable
        onUserDismiss={dismissProgress}
      >
        <span className="fontSize--s">
          {l10n.getString("receipts-upload-progress-success", {
            total,
            pending,
            unmatched,
          })}
        </span>
      </Alert>
    </div>
  );
};

export default UploadProgressSection;
